import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router';
import { api, ApiError } from '../api/client';
import {
    Button, Card, Field, Select, TextInput, Loading, ErrorNote, toast
} from '../components/ui';

interface Created {
    sessionId: number;
    candidateName: string;
    templateName: string;
    questionCount: number;
    questionsWereGenerated: boolean;
    tokenExpiresAt: string;
    interviewUrl: string;
    invitationSent: boolean;
    invitationMessage: string | null;
    invitationEmail: string | null;
}

export function InterviewCreate() {
    const [params] = useSearchParams();
    const [candidateId, setCandidateId] = useState(params.get('candidateId') ?? '');
    const [templateId, setTemplateId] = useState('');
    /** A `datetime-local` value: "2026-08-20T10:30", local time, no zone. Empty means unscheduled. */
    const [scheduledAt, setScheduledAt] = useState('');
    const [created, setCreated] = useState<Created | null>(null);
    const [error, setError] = useState<ApiError | null>(null);

    const candidates = useQuery({
        queryKey: ['candidates', ''],
        queryFn: () => api.get<{ id: number; fullName: string; email: string | null }[]>(
            '/api/admin/candidates')
    });

    const templates = useQuery({
        queryKey: ['templates'],
        queryFn: () => api.get<{ id: number; name: string; isDefault: boolean }[]>(
            '/api/admin/templates')
    });

    // Preselect the default so a recruiter in a hurry only has to pick a candidate.
    const defaultTemplate = templates.data?.find((t) => t.isDefault);
    const effectiveTemplate = templateId || (defaultTemplate ? String(defaultTemplate.id) : '');

    const create = useMutation({
        mutationFn: () => api.post<Created>('/api/admin/interviews', {
            candidateId: Number(candidateId),
            templateId: Number(effectiveTemplate),
            // new Date() reads the field as local time and toISOString converts to UTC, which is what
            // the column stores. Sending the bare "2026-08-20T10:30" would be read as UTC and shift
            // the appointment by the recruiter's offset.
            scheduledAt: scheduledAt ? new Date(scheduledAt).toISOString() : null
        }),
        onSuccess: (result) => { setCreated(result); setError(null); },
        onError: (caught: ApiError) => setError(caught)
    });

    if (candidates.isLoading || templates.isLoading) return <Loading />;

    if (created) {
        return (
            <div className="max-w-2xl space-y-6">
                <Card title="Interview created">
                    <p className="text-sm text-slate-700">
                        {created.candidateName} · {created.templateName} · {created.questionCount} questions
                        {created.questionsWereGenerated && ' (generated for this candidate)'}
                    </p>

                    <div className="mt-4">
                        <Field
                            label="The candidate's link"
                            hint={`Works until ${new Date(created.tokenExpiresAt).toLocaleString()}. `
                                + 'This is the only time it can be shown — only a hash of it is stored.'}
                        >
                            <div className="flex gap-2">
                                <TextInput
                                    readOnly
                                    value={created.interviewUrl}
                                    onFocus={(e) => e.target.select()}
                                />
                                <Button
                                    variant="secondary"
                                    onClick={async () => {
                                        await navigator.clipboard.writeText(created.interviewUrl);
                                        toast('Copied.');
                                    }}
                                >
                                    Copy
                                </Button>
                            </div>
                        </Field>
                    </div>

                    <p className={`mt-4 rounded-lg px-3 py-2 text-sm ${
                        created.invitationSent
                            ? 'bg-emerald-50 text-emerald-800'
                            : 'bg-amber-50 text-amber-800'
                    }`}>
                        {created.invitationSent
                            ? `Emailed to ${created.invitationEmail}.`
                            : created.invitationMessage}
                    </p>

                    <div className="mt-5 flex gap-2">
                        <Link to="/interviews"><Button variant="secondary">Back to interviews</Button></Link>
                        <Button
                            onClick={() => { setCreated(null); setCandidateId(''); }}
                        >
                            Create another
                        </Button>
                    </div>
                </Card>
            </div>
        );
    }

    return (
        <div className="max-w-lg">
            <Card heading title="New interview">
                <form
                    className="space-y-4"
                    onSubmit={(e) => { e.preventDefault(); create.mutate(); }}
                >
                    {error && <ErrorNote message={error.message} />}

                    <Field label="Candidate" error={error?.fieldError('candidateId')}>
                        <Select
                            value={candidateId}
                            onChange={(e) => setCandidateId(e.target.value)}
                            invalid={!!error?.fieldError('candidateId')}
                        >
                            <option value="">Choose a candidate…</option>
                            {(candidates.data ?? []).map((c) => (
                                <option key={c.id} value={c.id}>
                                    {c.fullName}{c.email ? ` — ${c.email}` : ' — no email'}
                                </option>
                            ))}
                        </Select>
                    </Field>

                    <Field label="Template" error={error?.fieldError('templateId')}>
                        <Select
                            value={effectiveTemplate}
                            onChange={(e) => setTemplateId(e.target.value)}
                            invalid={!!error?.fieldError('templateId')}
                        >
                            <option value="">Choose a template…</option>
                            {(templates.data ?? []).map((t) => (
                                <option key={t.id} value={t.id}>
                                    {t.name}{t.isDefault ? ' (default)' : ''}
                                </option>
                            ))}
                        </Select>
                    </Field>

                    <Field
                        label="Scheduled for (optional)"
                        hint="Recorded for your own reference. The link works from the moment it is created until it expires — it is not held back until this time."
                        error={error?.fieldError('scheduledAt')}
                    >
                        <TextInput
                            type="datetime-local"
                            value={scheduledAt}
                            onChange={(e) => setScheduledAt(e.target.value)}
                            className="w-64"
                            invalid={!!error?.fieldError('scheduledAt')}
                        />
                    </Field>

                    <p className="text-xs text-slate-500">
                        A candidate can only have one interview open at a time. The next one can be
                        created once this is approved or rejected.
                    </p>

                    <div className="flex gap-2">
                        <Button
                            type="submit"
                            busy={create.isPending}
                            disabled={!candidateId || !effectiveTemplate}
                        >
                            Create and send the link
                        </Button>
                        <Link to="/interviews">
                            <Button type="button" variant="secondary">Cancel</Button>
                        </Link>
                    </div>
                </form>
            </Card>
        </div>
    );
}
